const INSTALLER_PATHS = {
  default: "/downloads/mosida-giftshop-kiosk-setup.exe",
  win: "/downloads/mosida-giftshop-kiosk-setup.exe",
  mac: "/downloads/mosida-giftshop-kiosk.dmg",
} as const;

const readEnvPath = (key: string, fallback: string): string =>
  process.env[key]?.trim() || fallback;

export const isMobileDevice = (): boolean => {
  if (typeof navigator === "undefined" || typeof window === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent || "";
  const platform = navigator.platform || "";

  if (
    /Android|webOS|iPhone|iPod|iPad|Mobile|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent
    )
  ) {
    return true;
  }

  if (platform === "MacIntel" && navigator.maxTouchPoints > 1) {
    return true;
  }

  if (window.matchMedia("(pointer: coarse)").matches && window.innerWidth < 1200) {
    return true;
  }

  return false;
};

export const isIosDevice = (): boolean => {
  if (typeof navigator === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent;
  return (
    /iPhone|iPod|iPad/i.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

export const isAndroidDevice = (): boolean =>
  /Android/i.test(navigator.userAgent);

export const isIosInAppBrowser = (): boolean => {
  if (!isIosDevice()) {
    return false;
  }

  return /(FBAN|FBAV|Instagram|Line|Twitter|GSA|LinkedInApp|Snapchat)/i.test(
    navigator.userAgent
  );
};

export const canUseNativePwaInstall = (): boolean => {
  return typeof window !== "undefined" && window.isSecureContext === true;
};

export const getAndroidKioskInstallUrl = (): string => {
  const configured = process.env.REACT_APP_KIOSK_PWA_URL?.trim();

  if (configured) {
    return configured.replace(/\/+$/, "");
  }

  if (
    typeof window !== "undefined" &&
    window.isSecureContext &&
    window.location?.origin
  ) {
    return window.location.origin;
  }

  return "";
};

export const isDesktopDevice = (): boolean => !isMobileDevice();

export const kioskInstallerPaths = {
  default: readEnvPath(
    "REACT_APP_KIOSK_INSTALLER_URL",
    INSTALLER_PATHS.default
  ),
  win: readEnvPath(
    "REACT_APP_KIOSK_INSTALLER_URL_WIN",
    INSTALLER_PATHS.win
  ),
  mac: readEnvPath(
    "REACT_APP_KIOSK_INSTALLER_URL_MAC",
    INSTALLER_PATHS.mac
  ),
};

export const resolveInstallerUrl = (path: string): string => {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${normalizedPath}`;
  }

  return normalizedPath;
};

export const getResolvedKioskInstallerPath = (): string => {
  if (isMobileDevice()) {
    return "";
  }

  const userAgent = navigator.userAgent;

  if (/Windows/i.test(userAgent)) {
    return kioskInstallerPaths.win;
  }

  if (/Macintosh|Mac OS X/i.test(userAgent)) {
    return kioskInstallerPaths.mac;
  }

  return kioskInstallerPaths.default;
};

export const getResolvedKioskInstallerUrl = (): string => {
  const path = getResolvedKioskInstallerPath();
  return path ? resolveInstallerUrl(path) : "";
};

export const getInstallerFileName = (path: string): string => {
  const segment = path.split("/").pop();
  return segment || "mosida-giftshop-kiosk-installer";
};

export async function isInstallerAvailable(path: string): Promise<boolean> {
  if (!path || isMobileDevice()) {
    return false;
  }

  const url = resolveInstallerUrl(path);

  try {
    const headResponse = await fetch(url, { method: "HEAD" });
    if (headResponse.ok) {
      return true;
    }
  } catch {
    // Fall back to GET below.
  }

  try {
    const getResponse = await fetch(url, { method: "GET" });
    return getResponse.ok;
  } catch {
    return false;
  }
}

export function triggerInstallerDownload(path: string): boolean {
  if (!path || isMobileDevice()) {
    return false;
  }

  const url = resolveInstallerUrl(path);
  const fileName = getInstallerFileName(path);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.rel = "noopener";
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  link.remove();
  return true;
}
